import React, { useEffect } from 'react'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom'
import GlobalStyle from '@/common/styles/globalStyle'
import { Layout } from '@/common/layout'
import { useMySpaceNavigationConfig } from '@/config/navigationConfig'
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics'
import { initNaverSA } from '@/common/utils/naverAnalytics'

import Login from './pages/Login/Login'
import PopupLogin from './pages/Login/PopupLogin'
import SignUp from './pages/SignIn/SignUp'
import SpaceManagementPage from './pages/SpaceManagement/SpaceManagementPage'
import InvitationCompanyUserPage from './pages/invitation/InvitationCompanyUserPage'
import InvitationPitchingPage from './pages/invitation/InvitationPitchingPage'
import InvitationInvestReviewPage from './pages/invitation/InvitationInvestReviewPage'
import InvitationCompanyInfoPage from './pages/invitation/InvitationCompanyInfoPage'
import { Toaster } from 'sonner'

import NotificationPage from './pages/MyPage/NotificationPage'
import { authService } from './services/authService'
import { useAuthStore } from './store/useAuthStore'

import ResultPage from './components-common/inisis/ResultPage'
import InvitationRegularlyReportPage from './pages/invitation/InvitationRegularlyReportPage'
import DefaultPage from '@/DefaultPage'
import Logout from './pages/logout/Logout'
import NotFound from './pages/NotFound'
import ChannelPage from './pages/ChannelPage'
import ApplicationChanngelPage from './pages/invitation/ApplicationChannelPage'
import HealthCheck from './pages/HealthCheck'

import * as Sentry from '@sentry/react'
import ErrorBoundaryFallback from './components/error/ErrorBoundary'
import InvitationPortfolioPage from './pages/invitation/InvitationPortfolioPage'

import AccountSettingsPage from './pages/AccountSettings/AccountSettingsPage'
import NewCompanyRegistration from './pages/SpaceManagement/NewCompanyRegistration/NewCompanyRegistration'
import FindCompanyPage from './pages/SpaceManagement/FindCompany/FindCompanyPage'
import JoinApplyPage from './pages/Task/JoinApply/JoinApplyPage'
import EmailVerificationBanner from './components/EmailVerificationBanner'
import EmailVerificationPage from './pages/EmailVerification/EmailVerificationPage'
import CompanySpaceManagement from './pages/SpaceManagement/CompanySpaceManagement/CompanySpaceManagement'
import MySpacePage from './pages/MySpace/MySpacePage'
import EmailWhitelistManagement from './pages/_dev/EmailWhitelistManagement'
import ChangePasswordPage from './pages/ChangePassword/ChangePasswordPage'
import BillingCallbackPage from './pages/Billing/BillingCallbackPage'
import CardRegisterPopupPage from './pages/Billing/CardRegisterPopupPage'
import McpLogin from './pages/Login/McpLogin'

const AppContent = () => {
  const { logout, user, isEmailVerificationBannerVisible, setEmailVerificationBannerVisible } = useAuthStore();
  const location = useLocation();
  const navigationConfig = useMySpaceNavigationConfig();
  
  // Google Analytics 초기화 및 페이지뷰 추적
  useGoogleAnalytics();

  // 네이버 SA 초기화 (프로덕션에서만 동작)
  useEffect(() => {
    initNaverSA();
  }, []);

  const environment = import.meta.env.VITE_NODE_ENV;
  const version = import.meta.env.VITE_VERSION;

  console.log(`Environment: ${environment}, Version: ${version}`);

  if(environment === 'production') {
    console.log = () => {};
  }

  const setUserProfile = async () => {
    await authService.setUserProfile()
  }

  const checkLogin = async () => {
    if(location.pathname.includes("logout")) return;
    if(location.pathname.includes("mcp-login")) return;

    const response = await authService.checkLogin();
    console.trace(response);
    console.trace(location.pathname);
    if (response.success) {
      setUserProfile();
    } else {
      logout();
    }
  }

  if (environment === 'development' || environment === 'production') {
    Sentry.init({
      dsn: "https://13b45edba936cc269cd447598190c152@o4509484716785664.ingest.us.sentry.io/4509484726812672",
      sendDefaultPii: true
    });
  }

  useEffect(() => {
    checkLogin();
  }, [location.pathname])

  // 이메일 인증 배너 표시 조건 확인
  const shouldShowEmailVerificationBanner = 
    user && 
    !user.emailVerified && 
    isEmailVerificationBannerVisible &&
    !location.pathname.includes('/login') &&
    !location.pathname.includes('/signin') &&
    !location.pathname.includes('/logout') &&
    !location.pathname.includes('/settings') &&
    !location.pathname.includes('/email-verification');

  const isFullScreen = 
  location.pathname.includes('/mcp-login') ||
  location.pathname.includes('/login') || 
  location.pathname.includes('/signin') ||
  location.pathname.includes('/popup-login') || 
  location.pathname.includes('/change-password') ||
  location.pathname.includes('invitation') ||
  location.pathname.includes('request') || 
  location.pathname.includes('channel') ||
  location.pathname.includes('task') ||
  location.pathname.includes('dev') || 
  location.pathname.includes('email-verification') ||
  location.pathname.startsWith('/billing/');
  

  return (
    <Sentry.ErrorBoundary fallback={({resetError}) => <ErrorBoundaryFallback resetError={resetError} />}>
      {shouldShowEmailVerificationBanner && (
        <EmailVerificationBanner 
          userEmail={user.email}
          onClose={() => setEmailVerificationBannerVisible(false)}
        />
      )}
      <Layout
        navigationConfig={navigationConfig}
        isFullScreen={isFullScreen}
        headerProps={{
          spaceLabel: '마이스페이스',
          showNotification: false,
          showCompanyInfo: true,
        }}
      >
        <Routes>
          <Route path="/" element={<DefaultPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/popup-login" element={<PopupLogin />} />
          <Route path="/my-space" element={<MySpacePage />} />
          <Route path="/notifications" element={<NotificationPage />} />
          <Route path="/find-company" element={<FindCompanyPage />} />
          <Route path="/signin/signup" element={<SignUp />} />
          <Route path="/email-verification" element={<EmailVerificationPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/space-management" element={<SpaceManagementPage />} />
          <Route path="/space-management/new-company" element={<NewCompanyRegistration />} />
          <Route path="/space-management/company/:companyCode/*" element={<CompanySpaceManagement />} />              
          <Route path="/logout" element={<Logout />} />
          <Route path="/settings" element={<AccountSettingsPage />} />

          <Route path="/invitation/company-user" element={<InvitationCompanyUserPage />} />
          <Route path="/invitation/pitch-review" element={<InvitationPitchingPage />} />
          <Route path="/invitation/invest-review" element={<InvitationInvestReviewPage />} />
          <Route path='/invitation/portfolio' element={<InvitationPortfolioPage />} />

          <Route path="/request/company" element={<InvitationCompanyInfoPage />} />
          <Route path="/request/regularly-report" element={<InvitationRegularlyReportPage />} />
          
          <Route path="/channel/:channelId" element={<ChannelPage />} />
          <Route path="/channel/:channelId/apply" element={<ApplicationChanngelPage />} />
          
          <Route path="/inisis-auth/success" element={<ResultPage />} />
          <Route path="/inisis-auth/fail" element={<ResultPage />} />

          <Route path="/task/join-apply" element={<JoinApplyPage />} />
          <Route path="/billing/card-register" element={<CardRegisterPopupPage />} />
          <Route path="/billing/card-callback/success" element={<BillingCallbackPage type="success" />} />
          <Route path="/billing/card-callback/fail" element={<BillingCallbackPage type="fail" />} />
          <Route path="/mcp-login" element={<McpLogin />} />
          
          {/* Dev Only Routes - 일반 유저 접근 방지를 위한 복잡한 경로 */}
          {environment !== 'production' && <Route path="/dev/sys/internal/wl-mgmt-x7k9" element={<EmailWhitelistManagement />} />}
          
          <Route path="*" element={<NotFound/>} />
        </Routes>
      </Layout>
      <Toaster position="top-right" expand={false} richColors theme="system" />
    </Sentry.ErrorBoundary>
  )
}

const App: React.FC = () => {
  return (
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <GlobalStyle />
        <Routes>
          <Route path="/system/health/check/ifj93ksd-f3jf-2kds-f3fs-k34kjt32f8fa" element={<HealthCheck />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </BrowserRouter>
  )
}

export default App
